import {
    Avatar, Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    PostAddOutlined as CreatedIcon,
    EditOutlined as EditedIcon,
    PrintOutlined as PrintedIcon,
    Inventory2Outlined as PickedIcon,
    CheckCircleOutline as CompletedIcon,
    SettingsOutlined as SystemIcon,
} from '@mui/icons-material';

interface OrderLogEntry {
    timestamp: string | null;
    code: string | null;
    label: string;
    category: string;
    sign: string | null;
    printer: string | null;
}

interface Props {
    open: boolean;
    loading: boolean;
    orderNumber: string;
    customerName?: string;
    customerNumber?: string;
    entries: OrderLogEntry[];
    onClose: () => void;
}

const CATEGORY_ICON: Record<string, React.ElementType> = {
    Created: CreatedIcon,
    Edited: EditedIcon,
    Printed: PrintedIcon,
    Picked: PickedIcon,
    Completed: CompletedIcon,
    System: SystemIcon,
};

function formatTimestamp(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear().toString().slice(-2)} kl. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function OrderLogDialog({ open, loading, orderNumber, customerName, customerNumber, entries, onClose }: Props) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Ordrelogg – {orderNumber}
                    </Typography>
                    {(customerName || customerNumber) && (
                        <Typography variant="body2" color="text.secondary">
                            {customerName}{customerName && customerNumber ? ' · ' : ''}{customerNumber ? `Kunde ${customerNumber}` : ''}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, pb: 3 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Ingen hendelser funnet for denne ordren.
                    </Typography>
                ) : (
                    <Stack spacing={2.5}>
                        {entries.map((entry, idx) => {
                            const Icon = CATEGORY_ICON[entry.category] ?? SystemIcon;
                            const subtitleParts = [formatTimestamp(entry.timestamp), entry.sign].filter(Boolean);
                            const label = entry.category === 'Printed' && entry.printer
                                ? `${entry.label} på ${entry.printer}`
                                : entry.label;
                            return (
                                <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                    <Avatar sx={{ bgcolor: '#f0f0f0', color: '#666', width: 36, height: 36 }}>
                                        <Icon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {subtitleParts.join(' · ')}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}
